import React from 'react';
import PropTypes from 'prop-types';
import { useTable, usePagination, useFilters, useSortBy, useRowSelect } from 'react-table';
import IndeterminateCheckbox from 'components/IndeterminateCheckbox';
import FreeTextFilter from './Filters/FreeTextFilter';
import useResetSortBy from './Hooks/useResetSortBy';

const ReactTable = ({
  data,
  gridCriteria,
  onGridCriteriaChanged,
  onRowSelectionChanged,
  onRowExpandedStateChanged,
  actions,
  enableExpandCollapse,
  enableSelection,
  childRowPropertyName,
  getChildRowLayout,
  initialLoaded,
  loading,
  className,
  collapseHeight,
  enablePagination
}) => {
  const onSelectCheckboxClicked = (rows) => {
    gridCriteria.selectedItems = rows.map(d => d.original);
    if (typeof onRowSelectionChanged === 'function') {
      onRowSelectionChanged({ selectedItems: gridCriteria.selectedItems });
    }
  };

  const expandCollapse = {
    id: 'expand-collapse-column',
    Header: () => (''),
    // eslint-disable-next-line react/prop-types, react/no-unstable-nested-components
    Cell: ({ row }) => (
      <button
        type="button"
        className={ `btn btn-sm mr-0 ${ row.original.isLoadingDetails ? 'loading' : '' }` }
        title={ `${ childRowPropertyName && row.original[childRowPropertyName] == null ? 'Display' : 'Hide' } additional details` }
        onClick={ () => {
          if (enableExpandCollapse && onRowExpandedStateChanged) {
            onRowExpandedStateChanged(row, childRowPropertyName && row.original[childRowPropertyName] == null);
          }
        } }
      >
        { (childRowPropertyName && row.original[childRowPropertyName] != null) ? '▲' : '▼' }
      </button>
    )
  };

  const defaultColumn = React.useMemo(() => ({ Filter: FreeTextFilter }), []);
  const rowsFound = gridCriteria.itemCount > 0;

  const getPreSelectedRows = () => {
    if (data && data.length > 0 && gridCriteria && gridCriteria.selectedItems.length > 0) {
      const selectedIds = gridCriteria.selectedItems.map(v => v.itemId);

      const selectedData = data.map((v, i) => {
        const item = {
          key: i,
          value: selectedIds.includes(v.itemId)
        };
        return item;
      });

      const result = {};
      for (let i = 0; i < selectedData.length; i += 1) {
        if (selectedData[i].value) {
          result[selectedData[i].key] = selectedData[i].value;
        }
      }

      return result;
    }
    return [];
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setHiddenColumns,
    state: { pageIndex, sortBy, filters },
    setAllFilters,
    selectedFlatRows,
    resetSortBy,
    toggleAllRowsSelected
  } = useTable(
    {
      data,
      columns: gridCriteria.columnDefs,
      pageCount: gridCriteria.pageCount,
      defaultColumn,
      initialState: {
        pageIndex: gridCriteria.pageIndex,
        filters: gridCriteria.filters ?? [],
        sortBy: gridCriteria.sortBy,
        hiddenColumns: gridCriteria.columnDefs.filter(i => i.isVisible === false).map(i => i.accessor),
        selectedRowIds: getPreSelectedRows()
      },
      manualPagination: true,
      manualFilters: true,
      manualGlobalFilter: true,
      manualSortBy: true,
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    useResetSortBy,
    (hooks) => {
      hooks.allColumns.push((cols) => {
        const combinedCols = [];
        if (enableSelection) {
          combinedCols.push({
            id: 'selection-column',
            // eslint-disable-next-line react/no-unstable-nested-components
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox
                { ...getToggleAllRowsSelectedProps() }
              />
            ),
            // eslint-disable-next-line react/no-unstable-nested-components
            Cell: ({ row }) => (
              <IndeterminateCheckbox
                { ...row.getToggleRowSelectedProps() }
                className="cell-checkbox"
                data-item-id={ row.original.itemId }
              />
            )
          });
        }
        combinedCols.push(...cols);
        if (actions) {
          combinedCols.push(actions);
        }
        if (enableExpandCollapse && onRowExpandedStateChanged) {
          combinedCols.push(expandCollapse);
        }

        return combinedCols;
      });
    }
  );

  React.useEffect(() => {
    if (initialLoaded) {
      gotoPage(0);
      setAllFilters([]);
      resetSortBy();
      toggleAllRowsSelected(false);
    }
    setHiddenColumns(gridCriteria.columnDefs.filter(i => i.isVisible === false).map(i => i.accessor));
  }, [gridCriteria.columnDefs]);

  React.useEffect(() => {
    gotoPage(gridCriteria.pageIndex);
  }, [gridCriteria.pageIndex]);

  React.useEffect(() => {
    if (initialLoaded) {
      gotoPage(0);
      toggleAllRowsSelected(false);
    }
  }, [gridCriteria.itemCount]);

  React.useEffect(() => {
    onSelectCheckboxClicked(selectedFlatRows);
  }, [selectedFlatRows]);

  React.useEffect(() => {
    if (gridCriteria.pageIndex !== pageIndex
      || gridCriteria.sortBy !== sortBy
      || gridCriteria.filters !== filters) {
      const newGridCriteria = { ...gridCriteria, pageIndex, sortBy, filters, selectedItems: [] };
      onGridCriteriaChanged(newGridCriteria);
    }
  }, [gridCriteria.pageSize, pageIndex, sortBy, filters]);

  const getHeaderClassName = (index, col) => {
    if (enableSelection && index <= 0) {
      return 'checkbox-header-cell';
    }
    return col.id === 'expand-collapse-column' ? 'expand-header-cell' : col.className ?? 'col-10p';
  };

  const getCellClassName = (index, col) => {
    if (enableSelection && index <= 0) {
      return 'checkbox-cell';
    }
    return col.id === 'expand-collapse-column' ? 'expand-cell' : col.className ?? 'col-10p';
  };

  const getSortIcon = (column) => {
    if (column.isSorted) {
      if (column.isSortedDesc) {
        return <span className="icon icon-arrow-down">&nbsp;&nbsp;</span>;
      }
      return <span className="icon icon-arrow-up">&nbsp;&nbsp;</span>;
    }
    return <span className="icon icon-resize-vert">&nbsp;&nbsp;</span>;
  };

  const getLastRecordOnPage = () => {
    const lastRecord = (pageIndex * gridCriteria.pageSize) + page.length;
    return (lastRecord > gridCriteria.itemCount) ? gridCriteria.itemCount : lastRecord;
  };

  return (
    <div className="react-table-container">
      <table { ...getTableProps() } className={ `table table-striped table-vert-scroll react-table table-hover table-bordered ${ className ?? '' } ` }>
        <thead>
          { headerGroups.map((headerGroup, i) => (
            <tr key={ headerGroup.id } { ...headerGroup.getHeaderGroupProps() } className="noselect">
              { headerGroup.headers.map((column, j) => (
                <th { ...column.getHeaderProps() } key={ headerGroup.headers[j].id } className={ getHeaderClassName(j, headerGroup.headers[j]) }>
                  { column.render('Header') }
                  <span className="column-actions">
                    { column.canSort && ((j > 0 && j < headerGroup.headers.length - 1) || !enableSelection) && <span className="sort-span" { ...column.getHeaderProps(column.getSortByToggleProps()) }>
                      { getSortIcon(column) }
                    </span>}
                    { column.canFilter ? column.render('Filter') : null }
                  </span>
                </th>
              )) }
            </tr>
          )) }
        </thead>
        { !rowsFound && !loading && <tbody className={ collapseHeight ? 'height-auto' : '' }><tr className="no-rows"><td><div>No Rows to Show</div></td></tr></tbody> }
        { loading && <tbody><tr className="loading-row"><td><div className="loading loading-lg" /></td></tr></tbody> }
        { !loading && rowsFound && <tbody className={ collapseHeight ? 'height-auto' : '' } { ...getTableBodyProps() }>
          { page.map((row, i) => {
            prepareRow(row);
            return (
              <React.Fragment key={ row.id }>
                <tr key={ row.id } { ...row.getRowProps() }>
                  { row.cells.map((cell, j) => <td key={ cell.id } className={ getCellClassName(j, cell.column) } { ...cell.getCellProps() }>{ cell.render('Cell') }</td>) }
                </tr>
                { row.original[childRowPropertyName] && getChildRowLayout && <tr key={ `${ row.id }-addon` } className="child-row">
                  <td colSpan={ row.cells.length }>
                    <div className="container">
                      { getChildRowLayout(row.original[childRowPropertyName]) }
                    </div>
                  </td>
                </tr> }
              </React.Fragment>
            );
          }) }
        </tbody> }
      </table>
      { enablePagination && <div className="ag-paging-panel ag-font-style">
        <span id="eSummaryPanel" className="ag-paging-row-summary-panel">
          <span id="lbFirstRowOnPage">{gridCriteria.itemCount !== 0 ? (pageIndex * gridCriteria.pageSize) + 1 : 0}</span> to <span id="lbLastRowOnPage">{ getLastRecordOnPage() }</span> of <span id="lbRecordCount">{gridCriteria.itemCount}</span>
        </span>
        <span className="ag-paging-page-summary-panel">
          <button type="button" className="btn ag-paging-button" id="btFirst" disabled={ !canPreviousPage } onClick={ () => gotoPage(0) }>First</button>
          <button type="button" className="btn ag-paging-button" id="btPrevious" disabled={ !canPreviousPage } onClick={ () => previousPage() }>Previous</button>
          Page <span id="lbCurrent">{gridCriteria.itemCount !== 0 ? pageIndex + 1 : 0}</span> of <span id="lbTotal">{pageOptions.length}</span>&nbsp;&nbsp;
          <button type="button" className="btn ag-paging-button" id="btNext" disabled={ !canNextPage } onClick={ () => nextPage() }>Next</button>
          <button type="button" className="btn ag-paging-button" id="btLast" disabled={ !canNextPage } onClick={ () => gotoPage(pageCount - 1) }>Last</button>
        </span>
        <span id="ePageSize" className="ag-page-size">
          <select id="pageSize" className="form-select" value={ gridCriteria.pageSize } onChange={ (e) => { gotoPage(0); gridCriteria.pageSize = Number(e.target.value); onGridCriteriaChanged(gridCriteria); } }>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </span>
      </div> }
    </div>
  );
};

ReactTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()),
  gridCriteria: PropTypes.shape({
    selectedItems: PropTypes.arrayOf(PropTypes.shape()),
    itemCount: PropTypes.number,
    pageSize: PropTypes.number,
    pageIndex: PropTypes.number,
    pageCount: PropTypes.number,
    filters: PropTypes.arrayOf(PropTypes.shape()),
    sortBy: PropTypes.arrayOf(PropTypes.shape()),
    columnDefs: PropTypes.arrayOf(PropTypes.shape()),
  }),
  onGridCriteriaChanged: PropTypes.func,
  onRowSelectionChanged: PropTypes.func,
  onRowExpandedStateChanged: PropTypes.func,
  getToggleAllRowsSelectedProps: PropTypes.func,
  actions: PropTypes.shape(),
  row: PropTypes.shape(),
  enableExpandCollapse: PropTypes.bool,
  enableSelection: PropTypes.bool,
  childRowPropertyName: PropTypes.string,
  getChildRowLayout: PropTypes.func,
  initialLoaded: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  collapseHeight: PropTypes.bool,
  enablePagination: PropTypes.bool,
};

ReactTable.defaultProps = {
  data: null,
  gridCriteria: null,
  onGridCriteriaChanged: null,
  onRowSelectionChanged: null,
  onRowExpandedStateChanged: null,
  getToggleAllRowsSelectedProps: null,
  actions: null,
  row: null,
  enableExpandCollapse: true,
  enableSelection: false,
  childRowPropertyName: null,
  getChildRowLayout: null,
  initialLoaded: false,
  loading: true,
  className: null,
  collapseHeight: false,
  enablePagination: true,
};

export default ReactTable;
